import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type Account } from '@/api';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';
import { STALE_TIMES } from '@/api/queryClient';
import { useOptionalSession } from './session';
import { swatchInk } from '@/utils/swatchInk';

/**
 * Connected-account state.
 *
 * v1 has NO unified inbox (explicitly out of scope in the spec): exactly one
 * account is active at a time and every folder/message/calendar query is scoped
 * to `activeAccountId`.
 */
export interface AccountContextValue {
  accounts: Account[];
  activeAccount: Account | undefined;
  activeAccountId: string | undefined;
  setActiveAccountId: (id: string) => void;
  isLoading: boolean;
  error: Error | undefined;
  reload: () => void;
}

const AccountContext = createContext<AccountContextValue | null>(null);

const ACTIVE_ACCOUNT_KEY = 'ols-mail.active-account';

function readStoredAccountId(): string | undefined {
  try {
    return localStorage.getItem(ACTIVE_ACCOUNT_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function AccountProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  // Optional: this provider is also mounted bare in component tests.
  const sessionAccount = useOptionalSession()?.account;
  const [selectedId, setSelectedId] = useState<string | undefined>(readStoredAccountId);

  const query = useQuery({
    queryKey: queryKeys.accounts(),
    queryFn: ({ signal }) => api.accounts.list(signal),
    staleTime: STALE_TIMES.metadata,
  });

  const accounts = useMemo(() => query.data ?? [], [query.data]);

  /**
   * Default: the session's own mailbox.
   *
   * `session.account.id` is the primary account's derived id (ADR 0010), so it
   * is the right default even before `/accounts` resolves, and it survives a
   * backend restart or a datastore change — which matters because the
   * persisted query cache is keyed on it. `isPrimary` is the fallback for the
   * case where the session has not loaded yet.
   */
  const activeAccount = useMemo(
    () =>
      accounts.find((account) => account.id === selectedId) ??
      accounts.find((account) => account.id === sessionAccount?.id) ??
      accounts.find((account) => account.isPrimary) ??
      accounts[0],
    [accounts, selectedId, sessionAccount],
  );

  const setActiveAccountId = useCallback((id: string) => {
    setSelectedId(id);
    try {
      localStorage.setItem(ACTIVE_ACCOUNT_KEY, id);
    } catch {
      /* ignore */
    }
  }, []);

  const refetch = query.refetch;
  const reload = useCallback(() => void refetch(), [refetch]);

  const value = useMemo<AccountContextValue>(
    () => ({
      accounts,
      activeAccount,
      activeAccountId: activeAccount?.id,
      setActiveAccountId,
      // `isLoading` is the first load only; a background revalidation keeps the
      // previous accounts on screen instead of flashing skeletons.
      isLoading: query.isLoading,
      error: query.error ?? undefined,
      reload,
    }),
    [accounts, activeAccount, setActiveAccountId, query.isLoading, query.error, reload],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

/**
 * The accounts, or nothing outside a provider.
 *
 * For components that are mounted bare in tests and in popped-out windows, and
 * whose behaviour without the account list is simply "the single-mailbox one" —
 * Settings → General offering the unified-view choice only when there are two
 * mailboxes, for example. Everything that cannot work without an account keeps
 * using {@link useAccountContext}, which throws.
 */
export function useOptionalAccountContext(): AccountContextValue | undefined {
  return useContext(AccountContext) ?? undefined;
}

export function useAccountContext(): AccountContextValue {
  const context = useContext(AccountContext);
  if (!context) throw new Error('useAccountContext must be used inside <AccountProvider>');
  return context;
}

/** Stable per-account swatch, so an account keeps its colour across sessions. */
export function accountColorVar(account: Pick<Account, 'id' | 'color'>, index: number): string {
  if (account.color) return account.color;
  return `var(--color-account-${(index % 4) + 1})`;
}

/**
 * The custom properties an account's avatar needs: its swatch, and — when the
 * swatch is a colour the user picked rather than a token — the ink that reads
 * on it (utils/swatchInk.ts).
 */
export function accountAvatarVars(account: Pick<Account, 'id' | 'color'>, index: number): Record<string, string> {
  const ink = swatchInk(account.color);
  return {
    '--avatar-color': accountColorVar(account, index),
    ...(ink === undefined ? {} : { '--avatar-ink': ink }),
  };
}

export function initialsOf(value: string): string {
  const parts = value.replace(/@.*$/, '').split(/[.\s_-]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? value[0] ?? '?';
  const second = parts[1]?.[0] ?? '';
  return (first + second).toUpperCase();
}

/**
 * Mailboxes on another provider (ADR 0107): Gmail, Yahoo, iCloud, AOL.
 *
 * Such a mailbox has mail and nothing that needs our Poste or our DAV. Most
 * of what it lacks is already said by its capability flags (`sieve`,
 * `passwordChange`, `quota`). Calendar and contacts are not: those flags are
 * false for every mailbox today, the primary included, so the views ask by
 * provider instead and say so in words rather than firing requests that can
 * only answer 501.
 *
 * Here rather than in a leaf of its own: the account switcher (initial
 * bundle) and the lazy calendar and contacts chunks all import it, and a
 * separate module became a 0.2 KB initial chunk of its own.
 */

/** The provider's display name for an external mailbox; `undefined` for one of ours. */
export function externalProviderName(
  account: Pick<Account, 'provider' | 'providerName'> | undefined,
): string | undefined {
  if (account?.provider === undefined || account.provider === '') return undefined;
  return account.providerName !== undefined && account.providerName !== ''
    ? account.providerName
    : account.provider;
}

/** "Calendar and contacts aren't available for Gmail mailboxes." */
export function externalDavSentence(providerName: string): string {
  return `Calendar and contacts aren’t available for ${providerName} mailboxes.`;
}
